// KAAYA — AGT-16 Phase 2 native delivery via Capacitor Local Notifications.
//
// Runs inside the native Android/iOS shell (which loads the deployed web app as a
// remote URL). Every native call is guarded and dynamically imported so this file is a
// complete no-op in a plain browser / the Emergent preview iframe.
//
// Flow: initNotifications() asks permission + registers action types (Yes/No/Snooze +
// an inline "note" reply) → syncPlan() pulls GET /notifications/plan and schedules local
// notifications → a tapped action button POSTs to /notifications/respond, and the inline
// text reply POSTs to /notifications/text. Snooze reschedules locally.
//
// NOTE: the official plugin's inline text field (`input:true`) is honoured on iOS. On
// Android the plugin does not expose direct-reply, so the "Add note" action opens the app
// to chat instead (true Android inline reply needs a custom native RemoteInput helper —
// tracked as a follow-up).
import { api } from "@/lib/api";

const WATER = "kaaya_water";
const MEAL = "kaaya_meal";
const SYMPTOM = "kaaya_symptom";
const GENERIC = "kaaya_generic";
const CHANNEL_ID = "kaaya_nudges";

const CATEGORY_TO_TYPE = {
  INTERACTIVE_WATER: WATER,
  INTERACTIVE_MEAL: MEAL,
  INTERACTIVE_POST_MEAL: MEAL,
  SYMPTOM_FOLLOWUP: SYMPTOM,
};

let _LN = null;       // cached LocalNotifications module (null on web)
let _initialised = false;
let _navigate = null; // react-router navigate, for OPEN_APP / open_chat

async function _isNative() {
  try {
    const { Capacitor } = await import("@capacitor/core");
    return Capacitor.isNativePlatform();
  } catch (_e) {
    return false;
  }
}

async function _plugin() {
  if (_LN) return _LN;
  const mod = await import("@capacitor/local-notifications");
  _LN = mod.LocalNotifications;
  return _LN;
}

// A stable-ish 31-bit integer id for a notification (the plugin requires numeric ids).
function _numericId(str) {
  let h = 0;
  const s = String(str || Math.random());
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return Math.abs(h) % 2000000000 || 1;
}

async function _confirm(ln, conf) {
  if (!conf) return;
  try {
    await ln.schedule({
      notifications: [{
        id: _numericId("confirm-" + Date.now()),
        title: conf.title || "Done",
        body: conf.body || "",
        channelId: CHANNEL_ID,
        schedule: { at: new Date(Date.now() + 400) },
        extra: { kaaya: true, kind: "confirmation" },
      }],
    });
  } catch (_e) { /* best-effort */ }
}

// Handle a tapped action button (or the notification body).
async function _onAction(ln, evt) {
  try {
    const notif = evt.notification || {};
    const extra = notif.extra || {};
    if (!extra.kaaya) return;
    const actionId = evt.actionId; // 'tap' when the body was tapped
    const inputValue = evt.inputValue; // iOS inline reply text (undefined on Android)

    // Inline note reply (or the note button with no text on Android → open chat).
    if (actionId === "btn_note") {
      if (inputValue && inputValue.trim()) {
        try {
          const { data } = await api.post("/notifications/text", { text: inputValue.trim() });
          if (data?.action === "open_chat") {
            _navigate?.("/chat", { state: { prefill: data.prefill } });
          } else {
            await _confirm(ln, data?.confirmation);
          }
        } catch (_e) { /* ignore */ }
      } else {
        _navigate?.("/chat");
      }
      return;
    }

    // Body tap / OPEN_APP → just bring the app to the relevant screen.
    if (actionId === "tap" || actionId === "btn_open") {
      _navigate?.(extra.open_screen || "/chat");
      return;
    }

    // Find the tapped button's routing payload from the dispatch we scheduled.
    const btn = (extra.action_buttons || []).find((b) => b.button_id === actionId);
    if (!btn) { _navigate?.("/chat"); return; }

    if (btn.action_type === "OPEN_APP") {
      _navigate?.(btn.open_screen || "/chat");
      return;
    }
    if (btn.action_type === "SNOOZE") {
      const mins = btn.snooze_minutes || 60;
      await ln.schedule({
        notifications: [{
          id: notif.id || _numericId("snooze-" + Date.now()),
          title: notif.title, body: notif.body,
          channelId: CHANNEL_ID, actionTypeId: extra.action_type_id,
          schedule: { at: new Date(Date.now() + mins * 60000), allowWhileIdle: true },
          extra,
        }],
      });
      return;
    }

    // QUICK_LOG / SYMPTOM_RESPONSE → write back to the server.
    const { data } = await api.post("/notifications/respond", {
      notification_id: extra.notification_id,
      template_id: extra.template_id,
      category: extra.category,
      action_id: actionId,
      action_type: btn.action_type,
      data_write: btn.data_write || {},
      snooze_minutes: btn.snooze_minutes,
      open_screen: btn.open_screen,
    });
    await _confirm(ln, data?.confirmation);
  } catch (_e) { /* swallow — never crash the native shell */ }
}

export async function initNotifications(navigate) {
  _navigate = navigate || _navigate;
  if (_initialised) return true;
  if (!(await _isNative())) return false;
  try {
    const ln = await _plugin();
    const perm = await ln.checkPermissions();
    if (perm.display !== "granted") {
      const req = await ln.requestPermissions();
      if (req.display !== "granted") return false;
    }
    try {
      await ln.createChannel({
        id: CHANNEL_ID, name: "KAAYA reminders",
        description: "Meal, water and check-in reminders", importance: 4, visibility: 1,
      });
    } catch (_e) { /* iOS has no channels */ }

    const note = { id: "btn_note", title: "Add note", input: true, inputButtonTitle: "Send", inputPlaceholder: "Type a quick note…" };
    await ln.registerActionTypes({
      types: [
        { id: WATER, actions: [
          { id: "btn_250ml", title: "Log 250ml" },
          { id: "btn_500ml", title: "Log 500ml" },
          { id: "btn_later", title: "Later" },
        ]},
        { id: MEAL, actions: [
          { id: "btn_yes_log", title: "Yes, log it", foreground: true },
          { id: "btn_skipped", title: "Skipped" },
          note,
        ]},
        { id: SYMPTOM, actions: [
          { id: "btn_better", title: "Better now" },
          { id: "btn_resolved", title: "All clear" },
          note,
        ]},
        { id: GENERIC, actions: [ { id: "btn_open", title: "Open", foreground: true } ] },
      ],
    });

    await ln.addListener("localNotificationActionPerformed", (evt) => _onAction(ln, evt));
    _initialised = true;
    return true;
  } catch (_e) {
    return false;
  }
}

// Pull today's plan from the backend and (re)schedule local notifications.
export async function syncPlan() {
  if (!(await _isNative())) return { ok: true, native: false, scheduled: 0 };
  try {
    const ln = await _plugin();
    if (!_initialised) {
      const ok = await initNotifications(_navigate);
      if (!ok) return;
    }
    const tzOffset = new Date().getTimezoneOffset(); // minutes (IST = -330)
    const { data } = await api.get("/notifications/plan", { params: { tz_offset: tzOffset } });
    const plan = data?.plan || [];

    // Clear our previously-scheduled KAAYA notifications to avoid duplicates.
    try {
      const pending = await ln.getPending();
      const mine = (pending.notifications || []).filter((n) => n.extra?.kaaya && n.extra?.kind !== "confirmation");
      if (mine.length) await ln.cancel({ notifications: mine.map((n) => ({ id: n.id })) });
    } catch (_e) { /* ignore */ }

    const now = Date.now();
    const toSchedule = [];
    for (const d of plan) {
      const at = new Date(d.send_at);
      if (isNaN(at) || at.getTime() <= now + 5000) continue; // skip past / imminent
      const typeId = CATEGORY_TO_TYPE[d.category] || GENERIC;
      toSchedule.push({
        id: _numericId(d.notification_id),
        title: d.title,
        body: d.body,
        channelId: CHANNEL_ID,
        actionTypeId: typeId,
        schedule: { at, allowWhileIdle: true },
        extra: {
          kaaya: true,
          action_type_id: typeId,
          notification_id: d.notification_id,
          template_id: d.template_id,
          category: d.category,
          action_buttons: d.action_buttons || [],
          open_screen: (d.action_buttons || []).find((b) => b.open_screen)?.open_screen || "/chat",
        },
      });
    }
    if (toSchedule.length) await ln.schedule({ notifications: toSchedule });
    return { ok: true, scheduled: toSchedule.length, suppressed: !!data?.suppressed, reason: data?.suppression_reason || null };
  } catch (_e) {
    return { ok: false, scheduled: 0, error: true };
  }
}

// ---- Helpers used by the in-app Notifications test panel ----
export async function notifStatus() {
  if (!(await _isNative())) return { native: false, permission: "unavailable" };
  try {
    const ln = await _plugin();
    const perm = await ln.checkPermissions();
    return { native: true, permission: perm.display || "unknown" };
  } catch (_e) {
    return { native: true, permission: "unknown" };
  }
}

export async function requestNotifPermission() {
  if (!(await _isNative())) return "unavailable";
  try {
    const ln = await _plugin();
    const req = await ln.requestPermissions();
    if (req.display === "granted") await initNotifications(_navigate);
    return req.display || "unknown";
  } catch (_e) {
    return "unknown";
  }
}

// Fires a fully-interactive test notification ~10s out — proves the WHOLE loop:
// delivery → tap "Log 250ml" → real water log written server-side → "✓ Logged" confirmation.
export async function sendTestNotification() {
  if (!(await _isNative())) return { ok: false, reason: "not_native" };
  const ok = await initNotifications(_navigate);
  if (!ok) return { ok: false, reason: "permission" };
  try {
    const ln = await _plugin();
    const nid = "test-" + Date.now();
    const buttons = [
      { button_id: "btn_250ml", label: "Log 250ml", action_type: "QUICK_LOG", data_write: { type: "water", ml: 250 } },
      { button_id: "btn_500ml", label: "Log 500ml", action_type: "QUICK_LOG", data_write: { type: "water", ml: 500 } },
      { button_id: "btn_later", label: "Later", action_type: "SNOOZE", snooze_minutes: 60 },
    ];
    await ln.schedule({
      notifications: [{
        id: _numericId(nid),
        title: "KAAYA test 🔔 — tap “Log 250ml”",
        body: "Notifications work! Tap “Log 250ml” to log a real glass of water and see the whole loop.",
        channelId: CHANNEL_ID,
        actionTypeId: WATER,
        schedule: { at: new Date(Date.now() + 10000), allowWhileIdle: true },
        extra: {
          kaaya: true,
          kind: "test",
          action_type_id: WATER,
          notification_id: nid,
          template_id: "TEST_WATER",
          category: "INTERACTIVE_WATER",
          action_buttons: buttons,
          open_screen: "/tracker",
        },
      }],
    });
    return { ok: true };
  } catch (_e) {
    return { ok: false, reason: "schedule_failed" };
  }
}
